<template>
    <div ref="app" id="cz2"
        :class="[
            'cz2',
            {
                'cz2--no-outlines': hideOutlines,
                'cz2--loaded': loaded,
                'cz2--absolute': useAbsolute,
            }
        ]"
        @keydown="keyDown"
        @mousedown="mouseDown"
        :style="styles()"
    >
        <focus-lock :disabled="true">
            <customizer></customizer>

            <transition name="cz2__fade">
                <popup-leaving v-if="showExitConfirmation"></popup-leaving>
            </transition>

            <transition name="cz2__fade">
                <popup-done v-if="showDoneConfirmation"></popup-done>
            </transition>

            <!-- Confirm switch to group personalization experience -->
            <transition name="cz2__fade">
                <popup-group v-if="showGroupConfirmation"></popup-group>
            </transition>

            <!-- Confirm switch to individual experience -->
            <transition name="cz2__fade">
                <popup-group
                    v-if="showIndividualConfirmation"
                    switchTo="individual"
                ></popup-group>
            </transition>

            <transition name="cz2__fade">
                <popup-generic-notify
                    v-if="showGenericNotify"
                ></popup-generic-notify>
            </transition>

            <transition name="cz2__fade">
                <popup-generic-confirm
                    v-if="showGenericConfirm"
                ></popup-generic-confirm>
            </transition>

            <transition name="cz2__fade">
                <popup-tos v-if="showTosConfirmation"></popup-tos>
            </transition>

            <div v-if="globalBusy" class="cz2__global-busy">
                <div class="cz2__modal-popup">
                    <div class="cz2__modal-popup__body">
                        <focus-lock>
                            <div class="cz2__modal-popup__content" tabindex="0">
                                <div class="cz2__modal-popup__header cz2__type__header">
                                    {{$t('SAVING')}}
                                </div>

                                <div v-if="isGroupOrder" class="cz2__modal-popup__body-copy">
                                    {{$t('SAVING_GROUP')}}
                                </div>
                            </div>
                        </focus-lock>
                    </div>
                </div>
            </div>
        </focus-lock>
    </div>
</template>

<style lang="scss">
    @import "style/variables.scss";

    @import "style/fonts.scss";

    //TODO: TEST

    .cz2__overlay-lock {
        .modal-background {
            display: none !important;
        }

        .modal {
            display: none !important;
        }
    }

    #cz2.cz2 {
        position: relative;

        left: 0;
        top: 0;
        width: 100vw;

        --app-height: 100%;

        z-index: 10000;

        background: #fff;

        box-sizing: border-box;

        overflow: hidden;

        * {
            box-sizing: border-box;
        }

        @include respond-above(sm) {
            position: fixed;
            height: 100vh;
        }

        [data-lock] {
            position: relative;

            left: 0;
            top: 0;

            width: 100%;
            height: 100%;

            display: flex;

            flex-direction: column;

            @include respond-above(sm) {
                flex-direction: row;
                position: absolute;
            }
        }

        opacity: 0;
        pointer-events: none;
        transition: opacity $transition-fast ease-out;

        &.cz2--loaded {
            opacity: 1;
            pointer-events: auto;
        }

        &.cz2--absolute {
            position: relative;

            @include respond-above(sm) {
                position: absolute;
            }
        }

        &.cz2--no-outlines {
            *, a, button {
                outline: none !important;
            }

            :focus {
                outline: none !important;
            }
        }

        .cz2__global-busy {
            position: absolute;

            left: 0;
            top: 0;

            width: 100%;
            height: 100%;

            z-index: 10000;

            background: rgba(255, 255, 255, 0.6);

            cursor: wait;
        }
    }
</style>

<script>
    // eslint-disable-next-line
    import URL from 'url-polyfill';
    // eslint-disable-next-line
    import '@netless/canvas-polyfill';

    import FocusLock from 'vue-focus-lock';
    import interact from 'interactjs';

    import Customizer from './components/Customizer.vue';

    import PopupLeaving from './components/PopupLeaving.vue';
    import PopupDone from './components/PopupDone.vue';
    import PopupGroup from './components/PopupGroup.vue';

    import PopupGenericNotify from './components/PopupGenericNotify.vue';
    import PopupGenericConfirm from './components/PopupGenericConfirm.vue';

    import PopupTos from './components/PopupTos.vue';

    export default {
        props: {
            options: {
                type: Object,
                default: () => {},
            },
        },
        components: {
            FocusLock,
            Customizer,
            PopupLeaving,
            PopupDone,
            PopupGroup,
            PopupGenericNotify,
            PopupGenericConfirm,
            PopupTos,
        },
        data() {
            return {
                loaded: false,
                useAbsolute: false,

                hideOutlines: true,

                lockHeight: null,
            };
        },
        computed: {
            /**
             * Is the UX blocked?
             */
            globalBusy() {
                return this.$store.state.globalBusy;
            },

            /**
             * Is exit confirmation visible?
             */
            showExitConfirmation() {
                return this.$store.state.showExitConfirmation;
            },

            /**
             * Is done confirmation visible?
             */
            showDoneConfirmation() {
                return this.$store.state.showDoneConfirmation;
            },

            /**
             * Switching to group confirmation visible
             */
            showGroupConfirmation() {
                return this.$store.state.showGroupConfirmation;
            },

            /**
             * Switching to individual confirmation visible
             */
            showIndividualConfirmation() {
                return this.$store.state.showIndividualConfirmation;
            },

            /**
             * Show generic notification popup.
             */
            showGenericNotify() {
                return this.$store.state.showGenericNotify;
            },

            /**
             * Show generic confirmation popup.
             */
            showGenericConfirm() {
                return this.$store.state.showGenericConfirm;
            },

            /**
             * Show TOS confirmation popup.
             */
            showTosConfirmation() {
                return this.$store.state.showTosConfirmation;
            },

            /**
             * Shortcut to self-destruct flag.
             */
            selfDestruct() {
                return this.$store.state.selfDestruct;
            },

            /**
             * Is the current order for a group
             */
            isGroupOrder() {
                return this.$store.state.isGroupOrder;
            },
        },
        watch: {
            /**
             * Begin self-destruct sequence.
             */
            selfDestruct(v) {
                if (v) {
                    this.loaded = false;

                    setTimeout(() => {
                        this.$emit('selfDestruct');
                    }, 500);
                }
            },
        },
        mounted() {
            interact.addDocument(window.document, {
                events: { passive: false },
            });

            // Save options in all modules.
            this.$store.dispatch('setTranslations', (v) => this.$t(v));
            this.$store.dispatch('setOptions', this.options);
            this.$store.dispatch('setCustomizerOptions', this.options);
            this.$store.dispatch('setEcommerceOptions', this.options);

            // Once the popup is in DOM, load the product data.
            this.$store.dispatch('load', {
                options: this.options,
            });

            this.loaded = true;

            // Lock height.
            if (window.innerWidth < 1024) {
                this.lockHeight = window.innerHeight * (document.body.clientWidth / window.innerWidth);
            } else {
                this.lockHeight = null;
            }

            // Reset scroll position.
            this.oldBodyScroll = document.body.scrollTop || document.documentElement.scrollTop;
            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;

            document.body.style.height = this.lockHeight ? `${this.lockHeight}px` : `${this.$refs.app.offsetHeight}px`;

            this.$refs.app.style.setProperty('--app-height', `${this.lockHeight}px`);
            this.$store.dispatch('setHeight', this.lockHeight);

            // Replace fixed positioning with absolute.
            this.useAbsolute = true;

            // Hide everything so we have a clean page.
            document.body.classList.add('cz2__overlay-lock');

            window.addEventListener('resize', this.resize);

            // Because the scrolling state may have changed now with the body height adjusted, reevaluate sizing a moment later.
            this.heightTimer = setInterval(this.checkHeight, 100);

            this.$nextTick(() => {
                this.resize();
            });
        },
        beforeDestroy() {
            document.body.classList.remove('cz2__overlay-lock');
            document.body.style.height = 'auto';

            window.removeEventListener('resize', this.resize);

            if (this.oldBodyScroll) {
                document.body.scrollTop = this.oldBodyScroll;
                document.documentElement.scrollTop = this.oldBodyScroll;
            }

            if (this.heightTimer) {
                clearInterval(this.heightTimer);
            }
        },
        methods: {
            /**
             * Ensures full-screen dimensions on mobile.
             */
            checkHeight() {
                // Lock height.
                if (window.innerWidth < 1024) {
                    let h = window.innerHeight * (document.body.clientWidth / window.innerWidth);

                    const heights = [
                        this.$refs.app.querySelector('.cz2__navigation')?.offsetHeight ?? 0,
                        this.$refs.app.querySelector('.cz2__views')?.offsetHeight ?? 0,
                        Array.from(this.$refs.app.querySelectorAll('.cz2__sidebar')).find((x) => x.offsetHeight > 0)?.offsetHeight ?? 0,
                        this.$refs.app.querySelector('.cz2__footer')?.offsetHeight ?? 0,
                        this.$refs.app.querySelector('.cz2__footer__next')?.offsetHeight ?? 0,
                    ];

                    const nonIntersectHeight = heights.reduce((a, v) => a + v);

                    if (h < nonIntersectHeight) {
                        h = nonIntersectHeight;
                    }

                    if (this.lockHeight !== h) {
                        if (this.lockHeight == null || Math.abs(this.lockHeight - h) < 200) {
                            this.lockHeight = h;

                            document.body.style.height = `${this.lockHeight}px`;

                            this.$refs.app.style.setProperty('--app-height', `${this.lockHeight}px`);
                            this.$store.dispatch('setHeight', this.lockHeight);
                        }
                    }
                } else if (this.lockHeight) {
                    this.lockHeight = null;
                    document.body.style.height = 'auto';
                }
            },

            /**
             * Hides outlines on mouse actions.
             */
            mouseDown() {
                this.hideOutlines = true;
            },

            /**
             * Shows outlines again when keyboard is engaged.
             */
            keyDown() {
                this.hideOutlines = false;
            },

            resize() {
                if (this.resizeTimer) {
                    clearTimeout(this.resizeTimer);
                }

                this.resizeTimer = setTimeout(this.resizeDebounced, 0);
            },

            /**
             * Update sizing parameters.
             */
            resizeDebounced() {
                // eslint-disable-next-line
                console.log(`Resizing: height ${window.innerHeight}, client ${document.body.clientWidth}, window ${window.innerWidth}, lock ${this.lockHeight}`);

                const h = window.innerHeight * (document.body.clientWidth / window.innerWidth);

                if (this.lockHeight !== h) {
                    if (this.lockHeight == null || Math.abs(this.lockHeight - h) < 200) {
                        this.lockHeight = h;

                        this.$refs.app.style.setProperty('--app-height', `${this.lockHeight}px`);
                        this.$store.dispatch('setHeight', this.lockHeight);
                    }
                }

                document.body.style.height = `${this.$refs.app.offsetHeight}px`;

                this.$store.dispatch('setWindowWidth', window.innerWidth);
            },

            /**
             * Calculate widget styles.
             */
            styles() {
                const s = {
                };

                if (this.lockHeight && window.innerWidth >= 1024) {
                    s.height = `${this.lockHeight}px`;
                }

                return s;
            },
        },
    };
</script>
